import {
  createContext,
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../i18n/context.js';
import { hapticFeedback } from '../lib/haptics.js';
import { loadPinnedOrder, savePinnedOrder } from '../lib/pinnedOrder.js';
import {
  autoScrollDelta,
  DRAG_HOLD_MS,
  DRAG_HOLD_TOLERANCE_PX,
  DRAG_LIFT_PX,
  DRAG_MOUSE_THRESHOLD_PX,
  placeInPinnedOrder,
  resolveDropTarget,
  sameDropTarget,
  type ChatDragSource,
  type DragSource,
  type DropElement,
  type DropTarget,
} from '../lib/sidebarDnd.js';
import { useAnalyticsTracking } from '../state/analytics.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { Icon } from './Icon.js';

/**
 * Hold & drag for chats and folders in the sidebar.
 *
 * Touch / pen: rest a finger on a row → a light haptic tick (like long-pressing a
 * message) and the context menu opens. Keep the finger down and start moving and the
 * menu gives way to the drag: the row lifts off the list and follows the finger onto
 * the pinned group, a folder or between folders. Release without moving and the menu
 * simply stays. Mouse: just drag. Built on pointer events because HTML5 drag & drop
 * does not exist on touch screens.
 */

/** What is being dragged: a chat or a folder (reordering folders), plus how to draw its ghost. */
export type DragRowInfo = DragSource & {
  title: string;
  subtitle?: string;
};

export type ActiveDrag = DragRowInfo & {
  rect: { left: number; top: number; width: number; height: number };
};

export interface PressCallbacks {
  /** Touch hold in progress (before the menu opens) – used for the pressed look. */
  onPressChange?: (pressing: boolean) => void;
  /** The hold completed: show the context menu here. */
  onMenu: (position: { x: number; y: number }) => void;
  /** The finger started dragging while the menu was open: close it. */
  onMenuClose?: () => void;
  /** Dropped on a target. Chats are applied by the provider; folders hand the drop back here. */
  onDrop?: (target: DropTarget) => void;
}

interface SidebarDndValue {
  drag: ActiveDrag | null;
  target: DropTarget | null;
  startPress: (
    event: ReactPointerEvent<HTMLElement>,
    source: DragRowInfo,
    callbacks: PressCallbacks,
  ) => void;
  /** True once right after a drag / hold so the click that follows is ignored. */
  consumeSuppressedClick: () => boolean;
  lastPointerType: () => string;
  /** A hold / drag is in progress (the finger is busy and must not trigger other gestures). */
  isHeld: () => boolean;
}

const INERT: SidebarDndValue = {
  drag: null,
  target: null,
  startPress: (_event, _source, callbacks) => {
    void callbacks;
  },
  consumeSuppressedClick: () => false,
  lastPointerType: () => 'mouse',
  isHeld: () => false,
};

const SidebarDndContext = createContext<SidebarDndValue>(INERT);

export function useSidebarDnd(): SidebarDndValue {
  return useContext(SidebarDndContext);
}

interface Session {
  pointerId: number;
  pointerType: string;
  startX: number;
  startY: number;
  x: number;
  y: number;
  source: ActiveDrag;
  callbacks: PressCallbacks;
  /** pending: finger down · holding: menu open, finger still down · dragging: lifted */
  phase: 'pending' | 'holding' | 'dragging';
  offsetX: number;
  offsetY: number;
  scroller: HTMLElement | null;
  timer: number | undefined;
  raf: number;
}

/** A released ghost on its way back to the list (or dissolving into its drop target). */
interface Settle {
  id: number;
  drag: ActiveDrag;
  x: number;
  y: number;
  mode: 'return' | 'drop';
}

export function SidebarDndProvider({ section, children }: { section: string; children: ReactNode }) {
  const { api } = useSession();
  const { refreshConversations, toast } = useUI();
  const { t } = useI18n();
  const analytics = useAnalyticsTracking();
  const [drag, setDrag] = useState<ActiveDrag | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  const [settle, setSettle] = useState<Settle | null>(null);
  const ghostRef = useRef<HTMLDivElement>(null);

  // The gesture controller is created once; it reads the latest dependencies from here.
  const latest = useRef({ api, refreshConversations, toast, t, analytics, section });
  latest.current = { api, refreshConversations, toast, t, analytics, section };

  const controller = useMemo(() => {
    let session: Session | null = null;
    let currentTarget: DropTarget | null = null;
    let suppressClick = false;
    let suppressTimer: number | undefined;
    let pointerType = 'mouse';
    let settleId = 0;

    const setBothTargets = (next: DropTarget | null) => {
      currentTarget = next;
      setTarget(next);
    };

    const frame = () => {
      const s = session;
      if (!s || s.phase !== 'dragging') return;
      const ghost = ghostRef.current;
      if (ghost) {
        ghost.style.transform = `translate3d(${s.x - s.offsetX}px, ${s.y - s.offsetY}px, 0)`;
      }
      if (s.scroller) {
        const bounds = s.scroller.getBoundingClientRect();
        const delta = autoScrollDelta(s.y, bounds.top, bounds.bottom);
        if (delta !== 0) s.scroller.scrollTop += delta;
      }
      const el = document.elementFromPoint(s.x, s.y) as HTMLElement | null;
      const next = resolveDropTarget(el as unknown as DropElement | null, s.y, s.source);
      if (!sameDropTarget(next, currentTarget)) {
        setBothTargets(next);
        if (next) hapticFeedback('select');
      }
      s.raf = window.requestAnimationFrame(frame);
    };

    /** The row leaves the list and follows the finger. */
    const lift = (s: Session) => {
      if (session !== s || s.phase === 'dragging') return;
      s.phase = 'dragging';
      // Menu and drag are exclusive: the menu gives way as soon as the finger moves.
      s.callbacks.onMenuClose?.();
      hapticFeedback('select');
      setDrag(s.source);
      s.raf = window.requestAnimationFrame(frame);
    };

    /** The finger rested long enough: haptic tick + context menu (drag comes only if it moves on). */
    const hold = (s: Session) => {
      if (session !== s || s.phase !== 'pending') return;
      window.clearTimeout(s.timer);
      s.phase = 'holding';
      s.callbacks.onPressChange?.(false);
      hapticFeedback('tap');
      s.callbacks.onMenu({ x: s.startX, y: s.startY });
    };

    const teardown = (settleMode?: 'return' | 'drop') => {
      const s = session;
      if (!s) return;
      session = null;
      window.clearTimeout(s.timer);
      window.cancelAnimationFrame(s.raf);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onCancel);
      s.callbacks.onPressChange?.(false);
      currentTarget = null;
      if (settleMode && s.phase === 'dragging') {
        settleId += 1;
        setSettle({
          id: settleId,
          drag: s.source,
          x: s.x - s.offsetX,
          y: s.y - s.offsetY,
          mode: settleMode,
        });
      }
      setDrag(null);
      setTarget(null);
    };

    const suppressNextClick = () => {
      suppressClick = true;
      window.clearTimeout(suppressTimer);
      suppressTimer = window.setTimeout(() => {
        suppressClick = false;
      }, 450);
    };

    const commit = async (source: ChatDragSource, dropTarget: DropTarget) => {
      if (dropTarget.kind === 'folder-slot') return;
      const { api: client, refreshConversations: refresh, toast: notify, t: tr, analytics: stats } =
        latest.current;
      const currentSection = latest.current.section;
      try {
        if (dropTarget.kind === 'folder') {
          await client.updateConversation(source.id, { folderId: dropTarget.folderId });
          hapticFeedback('success');
          notify(tr('menu.addToFolder'), 'success');
          refresh();
          return;
        }
        if (dropTarget.kind === 'unpin-zone') {
          await client.updateConversation(source.id, { isPinned: false });
          savePinnedOrder(
            currentSection,
            loadPinnedOrder(currentSection).filter((id) => id !== source.id),
          );
          hapticFeedback('success');
          notify(tr('menu.unpin'), 'success');
          refresh();
          return;
        }
        const nextOrder = placeInPinnedOrder(loadPinnedOrder(currentSection), source.id, dropTarget);
        if (source.pinned) {
          // Already pinned: only the manual order changes, instantly and locally.
          savePinnedOrder(currentSection, nextOrder);
          hapticFeedback('success');
          return;
        }
        await client.updateConversation(source.id, { isPinned: true });
        savePinnedOrder(currentSection, nextOrder);
        stats.trackFavoriteAdded();
        hapticFeedback('success');
        notify(tr('menu.pin'), 'success');
        refresh();
      } catch {
        hapticFeedback('error');
        notify(tr('common.error'), 'error');
      }
    };

    function onMove(event: PointerEvent) {
      const s = session;
      if (!s || event.pointerId !== s.pointerId) return;
      s.x = event.clientX;
      s.y = event.clientY;
      const distance = Math.hypot(s.x - s.startX, s.y - s.startY);
      if (s.phase === 'pending') {
        if (s.pointerType === 'mouse') {
          if (distance > DRAG_MOUSE_THRESHOLD_PX) lift(s);
        } else if (distance > DRAG_HOLD_TOLERANCE_PX) {
          // The finger is scrolling the list, not holding.
          teardown();
        }
        return;
      }
      if (s.phase === 'holding' && distance > DRAG_LIFT_PX) lift(s);
    }

    function onUp(event: PointerEvent) {
      const s = session;
      if (!s || event.pointerId !== s.pointerId) return;
      s.x = event.clientX;
      s.y = event.clientY;
      if (s.phase === 'pending') {
        teardown(); // plain tap: the row's own click handler takes it from here
        return;
      }
      suppressNextClick();
      if (s.phase === 'holding') {
        teardown(); // released in place: the menu opened by the hold stays
        return;
      }
      const dropTarget = currentTarget;
      teardown(dropTarget ? 'drop' : 'return');
      if (!dropTarget) return;
      if (s.source.kind === 'folder') {
        hapticFeedback('success');
        s.callbacks.onDrop?.(dropTarget);
      } else {
        void commit(s.source, dropTarget);
      }
    }

    function onCancel() {
      teardown('return');
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') teardown('return');
    }

    return {
      /** Once the menu opened the finger must not scroll the list any more. */
      isHeld: () => session !== null && session.phase !== 'pending',
      lastPointerType: () => pointerType,
      consumeSuppressedClick: () => {
        const value = suppressClick;
        suppressClick = false;
        return value;
      },
      startPress: (
        event: ReactPointerEvent<HTMLElement>,
        source: DragRowInfo,
        callbacks: PressCallbacks,
      ) => {
        pointerType = event.pointerType || 'mouse';
        if (session || !event.isPrimary) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const row = event.currentTarget;
        const rect = row.getBoundingClientRect();
        const s: Session = {
          pointerId: event.pointerId,
          pointerType: pointerType,
          startX: event.clientX,
          startY: event.clientY,
          x: event.clientX,
          y: event.clientY,
          source: {
            ...source,
            rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          },
          callbacks,
          phase: 'pending',
          offsetX: event.clientX - rect.left,
          offsetY: event.clientY - rect.top,
          scroller: row.closest<HTMLElement>('.sidebar-body, .sidebar-menu-recent'),
          timer: undefined,
          raf: 0,
        };
        session = s;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        window.addEventListener('keydown', onKey);
        window.addEventListener('blur', onCancel);
        if (pointerType !== 'mouse') {
          callbacks.onPressChange?.(true);
          s.timer = window.setTimeout(() => hold(s), DRAG_HOLD_MS);
        }
      },
      teardown,
    };
  }, []);

  // Once the hold fired the finger must stay put or drag, not scroll the list. Touch
  // listeners on window are passive by default, so this one is explicitly not.
  useEffect(() => {
    const block = (event: TouchEvent) => {
      if (controller.isHeld() && event.cancelable) event.preventDefault();
    };
    window.addEventListener('touchmove', block, { passive: false });
    return () => {
      window.removeEventListener('touchmove', block);
      controller.teardown();
    };
  }, [controller]);

  const value = useMemo<SidebarDndValue>(
    () => ({
      drag,
      target,
      startPress: controller.startPress,
      consumeSuppressedClick: controller.consumeSuppressedClick,
      lastPointerType: controller.lastPointerType,
      isHeld: controller.isHeld,
    }),
    [controller, drag, target],
  );

  return (
    <SidebarDndContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <>
              {drag && <DragGhost drag={drag} ghostRef={ghostRef} />}
              {settle && (
                <SettleGhost
                  key={settle.id}
                  settle={settle}
                  onDone={() => setSettle((current) => (current?.id === settle.id ? null : current))}
                />
              )}
            </>,
            document.body,
          )
        : null}
    </SidebarDndContext.Provider>
  );
}

function GhostContent({ drag }: { drag: ActiveDrag }) {
  return drag.kind === 'folder' ? (
    <>
      <Icon name="folder" size={15} />
      <span className="folder-name">{drag.title}</span>
    </>
  ) : (
    <>
      <span className="conv-title">{drag.title}</span>
      {drag.subtitle && <span className="conv-time">{drag.subtitle}</span>}
    </>
  );
}

const DragGhost = memo(function DragGhost({
  drag,
  ghostRef,
}: {
  drag: ActiveDrag;
  ghostRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={ghostRef}
      className={`conv-drag-ghost${drag.kind === 'folder' ? ' conv-drag-ghost--folder' : ''}`}
      aria-hidden="true"
      style={{
        width: drag.rect.width,
        height: drag.rect.height,
        transform: `translate3d(${drag.rect.left}px, ${drag.rect.top}px, 0)`,
      }}
    >
      <GhostContent drag={drag} />
    </div>
  );
});

/** Where the dragged row currently sits in the list (its slot), if it is on screen. */
function findSourceRect(drag: ActiveDrag): { left: number; top: number } | null {
  const id = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(drag.id) : drag.id;
  const el =
    drag.kind === 'folder'
      ? document.querySelector<HTMLElement>(`[data-dnd-folder="${id}"] .folder-toggle`)
      : document.querySelector<HTMLElement>(`[data-dnd-conv="${id}"]`);
  const rect = el?.getBoundingClientRect();
  return rect && rect.width > 0 ? { left: rect.left, top: rect.top } : null;
}

/**
 * The lifted row, once released, glides back into its slot (or dissolves where it was
 * dropped) instead of vanishing, while the real row fades back in underneath.
 */
function SettleGhost({ settle, onDone }: { settle: Settle; onDone: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const { drag, mode } = settle;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const slot = mode === 'return' ? findSourceRect(drag) : null;
    // Commit the starting pose, then switch to the end pose so the transition runs.
    void el.getBoundingClientRect();
    el.classList.add('is-go');
    if (slot) el.style.transform = `translate3d(${slot.left}px, ${slot.top}px, 0)`;
    const timer = window.setTimeout(onDone, 420);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per settle
  }, []);

  return (
    <div
      ref={ref}
      className={`conv-drag-ghost is-settle is-${mode}${drag.kind === 'folder' ? ' conv-drag-ghost--folder' : ''}`}
      aria-hidden="true"
      style={{
        width: drag.rect.width,
        height: drag.rect.height,
        transform: `translate3d(${settle.x}px, ${settle.y}px, 0)`,
      }}
    >
      <GhostContent drag={drag} />
    </div>
  );
}

/** Wraps the pinned group so the whole block is a drop target while dragging. */
export function PinnedDropArea({ children }: { children: ReactNode }) {
  const { drag, target } = useSidebarDnd();
  const active = drag !== null && drag.kind !== 'folder' && !drag.pinned;
  const hot = active && (target?.kind === 'pinned-zone' || target?.kind === 'pinned-item');
  return (
    <div
      className={`dnd-pin-area${active ? ' is-armed' : ''}${hot ? ' is-target' : ''}`}
      data-dnd-pin-zone=""
    >
      {children}
    </div>
  );
}

/**
 * Wraps the unpinned chats: while a pinned chat is dragged this whole area becomes the
 * place to drop it to unpin it (with a hint at its top).
 */
export function UnpinDropArea({ children }: { children: ReactNode }) {
  const { drag, target } = useSidebarDnd();
  const { t } = useI18n();
  const armed = drag !== null && drag.kind !== 'folder' && drag.pinned;
  return (
    <div
      className={`dnd-unpin-area${armed ? ' is-armed' : ''}${armed && target?.kind === 'unpin-zone' ? ' is-target' : ''}`}
      data-dnd-unpin-zone=""
    >
      {armed && (
        <p className="dnd-unpin-hint">
          <Icon name="pin" size={14} />
          <span>{t('sidebar.dropToUnpin')}</span>
        </p>
      )}
      {children}
    </div>
  );
}

/**
 * "Drop here to pin / unpin" banner for screens without the pinned group. It floats over
 * the top of the list so nothing shifts under the finger when it appears.
 */
export function DropBanner({ mode }: { mode: 'pin' | 'unpin' }) {
  const { drag, target } = useSidebarDnd();
  const { t } = useI18n();
  if (!drag || drag.kind === 'folder' || drag.pinned !== (mode === 'unpin')) return null;
  const hot = target?.kind === (mode === 'pin' ? 'pinned-zone' : 'unpin-zone');
  return (
    <div className="dnd-pin-overlay">
      <div
        className={`dnd-pin-zone${hot ? ' is-target' : ''}`}
        {...(mode === 'pin' ? { 'data-dnd-pin-zone': '' } : { 'data-dnd-unpin-zone': '' })}
      >
        <Icon name="pin" size={16} />
        <span>{t(mode === 'pin' ? 'sidebar.dropToPin' : 'sidebar.dropToUnpin')}</span>
      </div>
    </div>
  );
}
